import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../../utils/api';
import { useAuthStore } from '../../store/authStore';
import { Send, MessageSquare, Search } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

export default function MessagesPage() {
  const { user, token } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeConv, setActiveConv] = useState(null);
  const [newMsg, setNewMsg] = useState('');
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_SOCKET_URL, { auth: { token } });
    socketRef.current.emit('join', user.id);
    socketRef.current.on('new_message', () => {
      queryClient.invalidateQueries('conversations');
      if (activeConv) queryClient.invalidateQueries(['messages', activeConv]);
    });
    return () => socketRef.current?.disconnect();
  }, [token, user.id]);

  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId) setActiveConv(convId);
  }, [searchParams]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  });

  const { data: convData } = useQuery('conversations', () =>
    api.get('/messages/conversations').then(r => r.data)
  );

  const { data: msgData } = useQuery(
    ['messages', activeConv],
    () => api.get(`/messages/${activeConv}`).then(r => r.data),
    { enabled: !!activeConv, refetchInterval: 3000 }
  );

  const sendMutation = useMutation(
    ({ convId, content }) => api.post('/messages/send', { conversation_id: convId, content }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['messages', activeConv]);
        queryClient.invalidateQueries('conversations');
        setNewMsg('');
      }
    }
  );

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    sendMutation.mutate({ convId: activeConv, content: newMsg.trim() });
  };

  const conversations = (convData?.conversations || []).filter(c =>
    !search || c.other_user_name?.toLowerCase().includes(search.toLowerCase()) || c.listing_title?.toLowerCase().includes(search)
  );

  const messages = msgData?.messages || [];
  const activeConvData = conversations.find(c => c.id === activeConv);

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-4" dir="rtl">
      {/* Conversations List */}
      <div className="w-80 flex-shrink-0 glass rounded-2xl flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-700/50">
          <h2 className="font-bold text-white mb-3">المحادثات</h2>
          <div className="relative">
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="form-input w-full pr-9 text-sm py-2"
              placeholder="بحث..."
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare size={36} className="text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">لا توجد محادثات</p>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => setActiveConv(conv.id)}
                className={`w-full text-right p-4 border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors ${activeConv === conv.id ? 'bg-primary-500/10 border-r-2 border-primary-500' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-400 font-bold text-sm">{conv.other_user_name?.[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm font-medium truncate">{conv.other_user_name}</span>
                      {conv.unread_count > 0 && (
                        <span className="bg-primary-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">{conv.unread_count}</span>
                      )}
                    </div>
                    <p className="text-slate-500 text-xs truncate">{conv.listing_title}</p>
                    {conv.last_message && <p className="text-slate-600 text-xs truncate mt-0.5">{conv.last_message}</p>}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Window */}
      <div className="flex-1 glass rounded-2xl flex flex-col overflow-hidden">
        {!activeConv ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare size={48} className="text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500">اختر محادثة للبدء</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-700/50 flex items-center gap-3">
              <div className="w-9 h-9 bg-primary-500/20 rounded-full flex items-center justify-center">
                <span className="text-primary-400 font-bold text-sm">{activeConvData?.other_user_name?.[0]}</span>
              </div>
              <div>
                <p className="text-white font-medium text-sm">{activeConvData?.other_user_name}</p>
                <p className="text-slate-500 text-xs">{activeConvData?.listing_title}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map(msg => {
                const isMine = msg.sender_id === user.id;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl text-sm ${isMine ? 'bg-primary-500 text-white rounded-tr-none' : 'bg-slate-700/70 text-slate-200 rounded-tl-none'}`}>
                      <p>{msg.content}</p>
                      <p className={`text-xs mt-1 ${isMine ? 'text-primary-200' : 'text-slate-500'}`}>
                        {format(new Date(msg.created_at), 'HH:mm')}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-slate-700/50 flex gap-3">
              <input
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                className="form-input flex-1"
                placeholder="اكتب رسالتك..."
              />
              <button type="submit" disabled={!newMsg.trim() || sendMutation.isLoading} className="btn-primary px-4 flex-shrink-0">
                <Send size={18} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
